<div class="page-wrapper">

  <a routerLink="/admin/organizations" class="link admin-detail-back">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M7.5 2.5L4 6L7.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
    All organizations
  </a>

  @if (isLoading) {
    <div class="org-summary">
      <div class="org-summary__head">
        <app-skeleton width="220px" height="28px" radius="4px"></app-skeleton>
        <app-skeleton width="320px" height="14px" radius="4px"></app-skeleton>
      </div>
      <dl class="org-summary__figures">
        @for (i of [1,2,3,4,5,6,7]; track i) {
          <div class="org-figure">
            <app-skeleton width="70px" height="14px" radius="4px"></app-skeleton>
            <app-skeleton width="90px" height="24px" radius="4px"></app-skeleton>
          </div>
        }
      </dl>
    </div>
  } @else if (notFound) {
    <div class="api-card api-card--full">
      <div class="empty-state">
        <p class="empty-state__title">Organization not found</p>
        <p class="empty-state__desc">It may have been renamed, or the link is wrong.</p>
        <a routerLink="/admin/organizations" class="btn--secondary empty-state__action">Back to organizations</a>
      </div>
    </div>
  } @else if (detail; as d) {

    <!-- ── Summary: identity on top, every figure in one hairline-split row below ── -->
    <div class="org-summary">
      <div class="org-summary__head">
        <div class="page-title">
          <h1 class="page-title__text">{{ d.organization.name }}</h1>
          @if (d.organization.billingExempt) {
            <span class="org-badge">Exempt</span>
          }
        </div>
        <p class="org-summary__meta">
          <span class="billing-table__mono">{{ d.organization.slug }}</span>
          <span class="org-summary__sep">·</span>
          Created {{ d.organization.createdAt | date:'mediumDate' }}
          @if (d.organization.cikisiClient?.name; as cikisiName) {
            <span class="org-summary__sep">·</span>
            Cikisi client <strong>{{ cikisiName }}</strong>
          }
        </p>
      </div>

      <dl class="org-summary__figures">
        <div class="org-figure" [class.org-figure--alert]="d.organization.balanceCents < 0">
          <dt class="org-figure__label">Balance</dt>
          <dd class="org-figure__value">{{ d.organization.balanceCents | money: d.organization.currency }}</dd>
          <dd class="org-figure__note">{{ d.organization.balanceCents < 0 ? 'Negative balance' : (d.organization.currency | uppercase) }}</dd>
        </div>
        <div class="org-figure">
          <dt class="org-figure__label">Requests</dt>
          <dd class="org-figure__value">{{ d.organization.lifetime.requests | number }}</dd>
          <dd class="org-figure__note">Since first call</dd>
        </div>
        <div class="org-figure">
          <dt class="org-figure__label">Credits</dt>
          <dd class="org-figure__value">{{ d.organization.lifetime.credits | number }}</dd>
          <dd class="org-figure__note">Since first call</dd>
        </div>
        <div class="org-figure">
          <dt class="org-figure__label">Cost</dt>
          <dd class="org-figure__value">{{ d.organization.lifetime.costCents | money: d.organization.currency }}</dd>
          <dd class="org-figure__note">Since first call</dd>
        </div>
        <div class="org-figure">
          <dt class="org-figure__label">Members</dt>
          <dd class="org-figure__value">{{ d.organization.membersCount | number }}</dd>
          <dd class="org-figure__note">Total</dd>
        </div>
        <div class="org-figure">
          <dt class="org-figure__label">API keys</dt>
          <dd class="org-figure__value">{{ d.organization.apiKeysCount | number }}</dd>
          <dd class="org-figure__note">Active</dd>
        </div>
        <div class="org-figure">
          <dt class="org-figure__label">Last call</dt>
          <dd class="org-figure__value">{{ d.organization.lifetime.lastCallAt ? (d.organization.lifetime.lastCallAt | date:'mediumDate') : 'Never' }}</dd>
          <dd class="org-figure__note">
            @if (d.organization.lifetime.firstCallAt) {
              First {{ d.organization.lifetime.firstCallAt | date:'mediumDate' }}
            } @else {
              No calls yet
            }
          </dd>
        </div>
      </dl>
    </div>

    <!-- ── Tabs ── -->
    <div class="org-tabs">
      <div class="org-tabs__list" role="tablist">
        <button type="button" role="tab" class="org-tabs__tab" [class.org-tabs__tab--active]="activeTab === 'members'" [attr.aria-selected]="activeTab === 'members'" (click)="setTab('members')">
          Members <span class="org-tabs__count">{{ d.members.length }}</span>
        </button>
        <button type="button" role="tab" class="org-tabs__tab" [class.org-tabs__tab--active]="activeTab === 'keys'" [attr.aria-selected]="activeTab === 'keys'" (click)="setTab('keys')">
          API keys <span class="org-tabs__count">{{ d.apiKeys.length }}</span>
        </button>
        <button type="button" role="tab" class="org-tabs__tab" [class.org-tabs__tab--active]="activeTab === 'calls'" [attr.aria-selected]="activeTab === 'calls'" (click)="setTab('calls')">
          Recent calls <span class="org-tabs__count">{{ d.recentCalls.length }}</span>
        </button>
      </div>
    </div>

    <!-- ── Members ── -->
    @if (activeTab === 'members') {
      <div class="api-card api-card--full org-panel" role="tabpanel">
        <div class="api-card__header">
          <div class="api-card__header-stack">
            <span class="api-card__title">Members</span>
            <span class="api-card__desc">{{ d.members.length }} of {{ d.organization.membersCount }} members in this organization.</span>
          </div>
          @if (d.members.length) {
            <div class="admin-filter-bar__search org-panel__search">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="7.25" cy="7.25" r="4.75" stroke="currentColor" stroke-width="1.6"/><path d="M10.75 10.75 14 14" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
              <input type="text" placeholder="Search members..." [(ngModel)]="memberSearch" />
            </div>
          }
        </div>
        @if (d.members.length) {
          <div class="admin-table-scroll">
            <table class="billing-table admin-stack admin-stack--members">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Sign-up</th>
                  <th>
                    <button type="button" class="org-sort" (click)="toggleJoinedSort()" [attr.aria-label]="'Sort by joined date, ' + (joinedSort === 'asc' ? 'oldest first' : 'newest first')">
                      Joined
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                        <path d="M2.5 4 5 1.5 7.5 4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" [attr.opacity]="joinedSort === 'asc' ? 1 : 0.35"/>
                        <path d="M2.5 6 5 8.5 7.5 6" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" [attr.opacity]="joinedSort === 'desc' ? 1 : 0.35"/>
                      </svg>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                @for (m of visibleMembers; track m.id) {
                  <tr>
                    <td>
                      <span class="org-member">
                        <span class="org-avatar" aria-hidden="true">{{ initials(m.user.name) }}</span>
                        {{ m.user.name }}
                      </span>
                    </td>
                    <td>{{ m.user.email }}</td>
                    <td>{{ m.role | titlecase }}</td>
                    <td>
                      @if (m.user.confirmed) {
                        <span class="badge--active org-dot-badge">Confirmed</span>
                      } @else {
                        <span class="txn-kind-badge txn-kind-badge--neutral">Unconfirmed</span>
                      }
                    </td>
                    <td>{{ m.createdAt | date:'mediumDate' }}</td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="5" class="org-panel__no-match">No members match “{{ memberSearch }}”.</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state"><p class="empty-state__title">No members yet.</p></div>
        }
      </div>
    }

    <!-- ── API keys ── -->
    @if (activeTab === 'keys') {
      <div class="api-card api-card--full org-panel" role="tabpanel">
        <div class="api-card__header">
          <div class="api-card__header-stack">
            <span class="api-card__title">API keys</span>
            <span class="api-card__desc">Active listed first, then revoked. Full secrets are never shown here.</span>
          </div>
        </div>
        @if (d.apiKeys.length) {
          <div class="admin-table-scroll">
            <table class="billing-table admin-stack admin-stack--keys">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Prefix</th>
                  <th>Status</th>
                  <th>Last used</th>
                  <th>Created</th>
                  <th class="org-key-row__expand-col"><span class="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody>
                @for (k of d.apiKeys; track k.id) {
                  <tr class="admin-row--link org-key-row" tabindex="0" aria-haspopup="dialog" title="Show calls made with this key" (click)="keyCallsModal.open(k.id)" (keydown.enter)="keyCallsModal.open(k.id)" (keydown.space)="$event.preventDefault(); keyCallsModal.open(k.id)">
                    <td>{{ k.name }}</td>
                    <td><span class="billing-table__mono">{{ k.prefix }}</span></td>
                    <td>
                      @if (k.isRevoked) {
                        <span class="txn-kind-badge txn-kind-badge--neutral">Revoked</span>
                      } @else {
                        <span class="badge--active org-dot-badge">Active</span>
                      }
                    </td>
                    <td [class.cell-status--muted]="!k.lastUsedAt">{{ k.lastUsedAt ? (k.lastUsedAt | date:'mediumDate') : 'Never' }}</td>
                    <td>{{ k.createdAt | date:'mediumDate' }}
                      @if (k.createdBy) {
                        <br><span class="billing-table__mono">{{ k.createdBy.name }}</span>
                      }
                    </td>
                    <td class="org-key-row__expand-col">
                      <span class="org-key-row__expand"><svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M9.5 2.5h4v4M13.5 2.5 9 7M6.5 13.5h-4v-4M2.5 13.5 7 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state"><p class="empty-state__title">No API keys yet.</p></div>
        }
      </div>
    }

    <!-- ── Recent calls ── -->
    @if (activeTab === 'calls') {
      <div class="api-card api-card--full org-panel" role="tabpanel">
        <div class="api-card__header">
          <div class="api-card__header-stack">
            <span class="api-card__title">Recent calls</span>
            <span class="api-card__desc">The last {{ d.recentCalls.length }} calls from this organization.</span>
          </div>
        </div>
        @if (d.recentCalls.length) {
          <div class="admin-table-scroll">
            <table class="billing-table admin-stack admin-stack--org-calls">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Source</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th class="col-num">Latency</th>
                  <th class="col-num">Credits</th>
                </tr>
              </thead>
              <tbody>
                @for (c of d.recentCalls; track c.id) {
                  <tr>
                    <td>{{ c.createdAt | date:'MMM d, y, HH:mm:ss' }}</td>
                    <td>{{ c.source }}</td>
                    <td class="cell-endpoint"><span class="cell-endpoint__method">{{ c.method }}</span> /{{ c.endpoint }}</td>
                    <td>
                      @if (c.error) {
                        <span class="txn-status-badge txn-status-badge--error">{{ c.status }}</span>
                      } @else {
                        <span class="cell-status--ok">{{ c.status }}</span>
                      }
                    </td>
                    <td class="col-num">{{ c.latencyMs | latency }}</td>
                    <td class="col-num">{{ c.credits }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="api-card__footer">
            <a [routerLink]="['/admin/calls']" [queryParams]="{ q: d.organization.slug }" class="link">
              View all calls
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M4.5 2.5L8 6L4.5 9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </a>
          </div>
        } @else {
          <div class="empty-state"><p class="empty-state__title">No calls yet.</p></div>
        }
      </div>
    }

  }

</div>
